// GCO: fork of Horizon 4.1.5 assets/quick-add.js. Re-diff against the vendor file on every Horizon upgrade.
//
// Why a fork (the element is defined by this module, so subclassing the vendor file is not possible):
//   - off-PDP quick add imports product-form.js and variant-picker.js on first interaction instead of preloading them
//     (openspec/specs/dynamic-module-loading); vendor 4.1.5 imports variant-picker statically;
//   - the modal content comes from the custom `gco-quick-add-content` section fetched at click time, cached in an LRU map
//     (the vendor fetches and parses the whole product page on hover);
//   - the bottom sheet dismisses with a swipe, and carousels re-measure after the morph (gco-carousel.js).
//
// Wiring: `@theme/quick-add` in the import map points here (snippets/scripts.liquid), and the core quick-add.js is no longer loaded.
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const MAX_CACHE_SIZE = 20;
// The dialog is shared across cards: only the latest interaction can write/open it.
let interactionGeneration = 0;
let dialogContentGeneration = 0;

/** @type {Promise<[unknown, unknown]> | null} */
let productFormScriptsPromise = null;

/**
 * Dynamically imports product-form.js and variant-picker.js when on non-product pages.
 * The promise is cached so the modules are requested once.
 * @returns {Promise<[unknown, unknown]> | void}
 */
function ensureProductFormScripts() {
  // On the product template both scripts are already loaded by scripts.liquid.
  if (window.Theme?.template?.name === 'product') return;
  if (productFormScriptsPromise) return productFormScriptsPromise;

  productFormScriptsPromise = Promise.all([import('@theme/product-form'), import('@theme/variant-picker')]).catch((error) => {
    productFormScriptsPromise = null;
    throw error;
  });
  return productFormScriptsPromise;
}

class LRUCache {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {number} */
  #maxSize;

  /** @param {number} maxSize */
  constructor(maxSize = MAX_CACHE_SIZE) {
    this.#maxSize = maxSize;
  }

  /** @param {string} key */
  get(key) {
    if (!this.#cache.has(key)) return undefined;
    const value = this.#cache.get(key);
    this.#cache.delete(key);
    this.#cache.set(key, /** @type {string} */ (value));
    return value;
  }

  /** @param {string} key @param {string} value */
  set(key, value) {
    if (this.#cache.has(key)) this.#cache.delete(key);
    else if (this.#cache.size >= this.#maxSize) {
      const oldestKey = this.#cache.keys().next().value;
      if (oldestKey) this.#cache.delete(oldestKey);
    }
    this.#cache.set(key, value);
  }

  has(/** @type {string} */ key) {
    return this.#cache.has(key);
  }

  clear() {
    this.#cache.clear();
  }
}

export class QuickAddComponent extends Component {
  #lifecycle = 0;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {LRUCache} */
  #cache = new LRUCache(MAX_CACHE_SIZE);
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#cartUpdateAbortController.signal.aborted) this.#cartUpdateAbortController = new AbortController();

    this.addEventListener('submit', this.#loadDirectAdd, true);
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycle++;
    this.removeEventListener('submit', this.#loadDirectAdd, true);
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Clears the cached sections when the cart changes (availability may have moved).
   */
  #handleCartUpdate = () => {
    this.#cache.clear();
  };

  /**
   * Retired: the vendor product card calls this on pointerenter / focusin / variant select to prefetch the whole product page.
   * The modal content is fetched at click time (`fetchQuickAddSection`), so the hover prefetch stays off, and the vendor card can
   * keep calling it unguarded.
   * @returns {Promise<null>}
   */
  async fetchProductPage() {
    return null;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  // Single-variant cards submit directly rather than opening the modal. Upgrade their form on first submit too.
  #directAddPending = false;
  #loadDirectAdd = async (event) => {
    if (customElements.get('product-form-component') || !(event.target instanceof HTMLFormElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#directAddPending) return;
    this.#directAddPending = true;
    const lifecycle = this.#lifecycle;
    this.setAttribute('aria-busy', 'true');
    try {
      await ensureProductFormScripts();
      if (this.isConnected && lifecycle === this.#lifecycle && event.target.isConnected) {
        event.target.requestSubmit(event.submitter ?? undefined);
      }
    } catch (error) {
      console.warn('[quick-add] Direct-add modules unavailable; the next submit can retry.', error);
    } finally {
      this.#directAddPending = false;
      this.removeAttribute('aria-busy');
    }
  };
  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;
    if (this.dataset.usesSellingPlans === 'true') {
      window.location.href = currentUrl;
      return;
    }
    const generation = ++interactionGeneration;
    const lifecycle = this.#lifecycle;
    const current = () => this.isConnected && lifecycle === this.#lifecycle && generation === interactionGeneration &&
      this.productPageUrl === currentUrl;
    this.setAttribute('aria-busy', 'true');
    try {
      await ensureProductFormScripts();
      if (!current()) return;
      let productHtml = this.#cache.get(currentUrl);
      if (!productHtml) {
        productHtml = await this.fetchQuickAddSection(currentUrl);
        if (!current()) return;
        if (productHtml) this.#cache.set(currentUrl, productHtml);
      }
      if (!productHtml) return;
      await this.updateQuickAddModal(productHtml, current);
      if (current()) this.#openQuickAddModal();
    } finally {
      this.removeAttribute('aria-busy');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the quick add section content (the custom `gco-quick-add-content` section, not the full product page)
   * @param {string} productPageUrl - The URL of the product page the section is rendered for
   * @returns {Promise<string | null>}
   */
  async fetchQuickAddSection(productPageUrl) {
    if (!productPageUrl) return null;

    const url = new URL(productPageUrl);
    url.searchParams.set('section_id', 'gco-quick-add-content');

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch quick add section: HTTP error ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Updates the modal content with the fetched HTML
   * @param {string} html - The section HTML
   */
  async updateQuickAddModal(html, current = () => this.isConnected) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!html || !modalContent || !current()) return;

    dialogContentGeneration = interactionGeneration;
    morph(modalContent, html);

    // carousel-component (gco-carousel.js) must re-measure after the morph
    requestAnimationFrame(() => {
      if (!current()) return;
      modalContent.querySelectorAll('carousel-component').forEach((el) => {
        if (typeof el.reinit === 'function') el.reinit();
      });
    });

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

// Swipe-down-to-dismiss bottom sheet (touch handlers below), drag handle rendered by gco-quick-add-modal.
class QuickAddDialog extends DialogComponent {
  #selection = 0;
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #touchStartX = 0;
  /** @type {number} */
  #touchStartY = 0;
  /** @type {number} */
  #lastTouchY = 0;
  /** @type {number} */
  #lastTouchTime = 0;
  /** @type {number} */
  #velocity = 0;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #dragConfirmed = false;

  /** Minimum distance to trigger close if velocity is low */
  static SWIPE_THRESHOLD = 100;
  /** Minimum velocity (px/ms) to trigger close */
  static VELOCITY_THRESHOLD = 0.6;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    this.#initDragHandle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selection++;
    this.#abortController.abort();
  }

  #initDragHandle() {
    const dialog = this.refs.dialog;
    const handle = /** @type {HTMLElement | null} */ (this.refs.dragHandle);
    if (!dialog || !handle) return;

    const { signal } = this.#abortController;

    handle.addEventListener('click', () => this.closeDialog(), { signal });

    dialog.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
    dialog.addEventListener('touchmove', this.#onTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
  }

  /**
   * @param {TouchEvent} e
   */
  #onTouchStart = (e) => {
    if (!isMobileBreakpoint()) return;

    const touch = e.touches[0];
    if (!touch) return;

    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#lastTouchY = touch.clientY;
    this.#lastTouchTime = Date.now();
    this.#isDragging = true;
    this.#dragConfirmed = false;
    this.#velocity = 0;
  };

  /**
   * @param {TouchEvent} e
   */
  #onTouchMove = (e) => {
    if (!this.#isDragging || !isMobileBreakpoint()) return;

    const touch = e.touches[0];
    if (!touch) return;

    const deltaY = touch.clientY - this.#touchStartY;
    const deltaX = touch.clientX - this.#touchStartX;
    const currentTime = Date.now();
    const timeDelta = currentTime - this.#lastTouchTime;

    if (timeDelta > 0) {
      this.#velocity = (touch.clientY - this.#lastTouchY) / timeDelta;
    }
    this.#lastTouchY = touch.clientY;
    this.#lastTouchTime = currentTime;

    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    if (!this.#dragConfirmed) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.#isDragging = false;
        return;
      }

      if (dialog.scrollTop > 5) {
        return;
      }

      if (deltaY > 5) {
        this.#dragConfirmed = true;
        dialog.style.transition = 'none';
        dialog.style.animation = 'none';
      } else {
        return;
      }
    }

    if (this.#dragConfirmed) {
      e.preventDefault();
      const moveY = Math.max(0, deltaY);
      dialog.style.transform = `translateY(${moveY}px)`;

      const opacity = Math.max(0, 1 - moveY / (window.innerHeight * 0.5));
      dialog.style.setProperty('--backdrop-opacity-multiplier', opacity.toString());
    }
  };

  /**
   * @param {TouchEvent} e
   */
  #onTouchEnd = (e) => {
    if (!this.#isDragging || !this.#dragConfirmed) {
      this.#isDragging = false;
      return;
    }

    this.#isDragging = false;
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    const deltaY = (e.changedTouches[0]?.clientY ?? 0) - this.#touchStartY;

    if (deltaY > QuickAddDialog.SWIPE_THRESHOLD || this.#velocity > QuickAddDialog.VELOCITY_THRESHOLD) {
      this.#dismissFromDrag();
    } else {
      dialog.style.transition = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)';
      dialog.style.transform = '';
      dialog.style.removeProperty('--backdrop-opacity-multiplier');
    }
  };

  async #dismissFromDrag() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#isDragging = false;
    this.#dragConfirmed = false;

    dialog.style.removeProperty('--backdrop-opacity-multiplier');

    await this.closeDialog();

    dialog.style.transform = '';
    dialog.style.transition = '';
    dialog.style.animation = '';
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    // A queued event may arrive from old content while the next card is still loading.
    const generation = dialogContentGeneration;
    const signal = this.#abortController.signal;
    event.promise
      ?.then(({ detail }) => {
        if (!this.isConnected || signal.aborted || generation !== interactionGeneration ||
          generation !== dialogContentGeneration || detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * Keeps the gallery and the product links of the modal in step with the selected variant.
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    const generation = interactionGeneration;
    const selection = ++this.#selection;
    const picker = event.target;
    let productId = picker?.dataset?.productId;
    const current = () => this.isConnected && generation === interactionGeneration && generation === dialogContentGeneration && selection === this.#selection &&
      picker?.isConnected && picker === this.querySelector('variant-picker') && picker.dataset.productId === productId;
    event.promise
      .then(({ detail }) => {
        // Native VariantPicker mutates its identity before resolving a combined listing.
        // Authenticate that transition against this selection and its response, not an
        // arbitrary later mutation of the same picker node.
        const next = detail?.newProduct;
        const responsePicker = detail?.html?.querySelector('variant-picker');
        if (next?.id && next.url && event.detail?.connectedProductUrl &&
            new URL(event.detail.connectedProductUrl, window.location.origin).pathname === new URL(next.url, window.location.origin).pathname &&
            detail.productId === next.id && picker?.dataset.productId === next.id &&
            picker.dataset.productUrl === next.url && responsePicker?.dataset.productId === next.id &&
            responsePicker.dataset.productUrl === next.url) productId = next.id;
        if (!current()) return;
        const html = detail?.html;
        if (!html) return;

        const galleryContainer = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add-content__media'));
        const newGallerySource = html.querySelector('.quick-add-content__media');

        if (galleryContainer && newGallerySource) {
          morph(galleryContainer, newGallerySource);

          requestAnimationFrame(() => {
            if (!current() || galleryContainer !== this.querySelector('.quick-add-content__media')) return;
            galleryContainer.querySelectorAll('carousel-component').forEach((el) => {
              if (typeof el.reinit === 'function') el.reinit();
            });
          });
        }

        const variantPicker = html.querySelector('variant-picker');
        const productUrl = variantPicker?.dataset?.productUrl;
        if (!productUrl) return;

        const variantId = detail.resource?.id;
        const url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);

        const href = url.pathname + url.search;
        const viewDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-content__view-details'));
        const productTitleLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-content__title'));

        if (viewDetailsLink) viewDetailsLink.href = href;
        if (productTitleLink) productTitleLink.href = href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    interactionGeneration++;
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
